import { useTranslation } from 'react-i18next';

import { Separator } from '@/components/ui/separator';
import { formatHoursToTimeString } from '@/helpers';

import { ProjectTimeTrackingSummary } from '../../types/time-tracking';
import { ProjectBreakdownRow } from './project-breakdown-row';

interface ProjectsThisMonthCardProps {
  projects: ProjectTimeTrackingSummary[];
}

export function ProjectsThisMonthCard({ projects }: ProjectsThisMonthCardProps) {
  const { t } = useTranslation('authenticated.timeTracking');

  const totalTracked = projects.reduce((sum, p) => sum + p.trackedHours, 0);
  const totalBooked = projects.reduce((sum, p) => sum + p.bookedHours, 0);

  return (
    <div className="flex w-full flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {t('monthView.projectsThisMonth.title')}
      </h3>
      {projects.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('monthView.projectsThisMonth.empty')}</p>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {projects.map(p => {
              const isAllBackgroundWork =
                p.bookedBreakdown.length > 0 && p.bookedBreakdown.every(b => b.isBackgroundWork);
              return (
                <ProjectBreakdownRow
                  key={p.project.projectId}
                  item={{
                    projectId: p.project.projectId,
                    projectName: p.project.projectName,
                    colorHex: p.project.colorHex,
                    trackedHours: p.trackedHours,
                    bookedHours: p.bookedHours,
                    isBackgroundWork: isAllBackgroundWork,
                  }}
                  indented
                />
              );
            })}
          </div>

          <Separator />

          <div className="flex items-baseline justify-between text-[12px]">
            <span className="font-medium text-muted-foreground">
              {t('monthView.projectsThisMonth.totalsLabel')}
            </span>
            <span className="font-semibold tabular-nums">
              {t('monthView.projectsThisMonth.totalsValue', {
                tracked: formatHoursToTimeString(totalTracked),
                booked: formatHoursToTimeString(totalBooked),
              })}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
